/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len */

// pcg-core: Coherent Energy research comparison (RESEARCH-ONLY; new in the
// v0.8.1-dev.18 handoff — pcg_viewer_v05_build.py template functions
// coherentEnergyV1 / visibleEnergyGuide / energySweepKey / recommendedEnergyBand;
// handoff v0.8.3 added the display-only S1/S2 boundary continuation to
// coherentEnergyV1 and the marker-overlap preview branch of visibleEnergyGuide,
// which lives in ./marker-overlap.js here).
//
// Extraction discipline matches the rest of pcg-core (see MODULE-MAP.md):
// function bodies are verbatim modulo the systematic global->argument edits —
//   D                    -> ctx.payload
//   D._coherentEnergyV11 -> ctx.cache.coherentEnergyV11 (per-band memo)
//   ENERGY_BANDS         -> energyBandMap(ctx)          (decoded payload.energyBands)
//   ENERGY_TEXTURE_BANDS -> energyTextureBandMap(ctx)   (decoded payload.energyTextureBands)
//   MURMUR               -> ctx.murmurTrace
//   TURBULENT_RF_MODEL   -> ctx.turbulentModel
//   analysisQuality()    -> analysisQuality(ctx)
//   state.energyBand     -> explicit bandKey argument
//   state.energyGuideMode-> explicit energyGuideMode argument (visibleEnergyGuide)
//   valuesForWindow(t,a,z)-> valuesForWindow(t,a,z,duration)
//
// Payloads without research bands (dev.17-era guides): the prototype's
// loadRawOnlyPackage fallback `ENERGY_BANDS = {s100:{trace:MURMUR,rangeHz:[100,300]}}`
// is applied here for ANY payload that ships no energyBands, so the released
// 100-300 Hz Energy layer keeps rendering for pre-dev.18 guide payloads.
//
// GUARDRAIL: everything in this module is research-only. It feeds the research
// triage/audit surfaces and the Energy layer's coherent overlay; it must never
// feed Provisional Results values or the export builder's released fields.

import { mean, median, percentile, valuesForWindow } from "./stats"
import { adaptiveEnergyThreshold, energyStrengthTier } from "./energy"
import { cycleAuditIds } from "./cycles"
import { decUnsigned } from "./traces"
import { analysisQuality } from "./analysis-quality"

// b64 band map -> {trace, rangeHz} map (prototype top-level ENERGY_BANDS decode).
function decodeBandMap(raw) {
  const out = {}
  Object.entries(raw || {}).forEach(([key, value]) => {
    out[key] = { trace: decUnsigned(value), rangeHz: value.range_hz || null }
  })
  return out
}

export function energyBandMap(ctx) {
  if (!ctx.cache.energyBandMap) {
    const decoded = decodeBandMap(ctx.payload.energyBands)
    if (!Object.keys(decoded).length && ctx.murmurTrace && ctx.murmurTrace.length) {
      // Prototype loadRawOnlyPackage parity: released 100-300 Hz trace as the
      // only band when the payload predates the dev.18 research sweep.
      decoded.s100 = { trace: ctx.murmurTrace, rangeHz: [100, 300] }
    }
    ctx.cache.energyBandMap = decoded
  }
  return ctx.cache.energyBandMap
}

export function energyTextureBandMap(ctx) {
  if (!ctx.cache.energyTextureBandMap) {
    ctx.cache.energyTextureBandMap = decodeBandMap(ctx.payload.energyTextureBands)
  }
  return ctx.cache.energyTextureBandMap
}

export function energySweepKey(lowHz) {
  return `s${String(Math.max(35, Math.min(250, Math.round(Number(lowHz) / 5) * 5))).padStart(3, "0")}`
}

export function coherentEnergyV1(ctx, bandKey = "s100") {
  ctx.cache.coherentEnergyV11 = ctx.cache.coherentEnergyV11 || {}
  if (ctx.cache.coherentEnergyV11[bandKey]) return ctx.cache.coherentEnergyV11[bandKey]
  const D = ctx.payload
  const ENERGY_BANDS = energyBandMap(ctx)
  const ENERGY_TEXTURE_BANDS = energyTextureBandMap(ctx)
  const TURBULENT_RF_MODEL = ctx.turbulentModel
  const quality = analysisQuality(ctx)
  const acceptedCycles = quality.summaryCycles || []
  const acceptedCycleIds = quality.selectedTimingCycleIds || []
  function recordingCoveragePct(cycleList) {
    if (!cycleList.length || !D.duration) return 0
    const start = Math.min(...cycleList.map((c) => c.s1.t))
    const end = Math.max(...cycleList.map((c) => c.nextS1.t))
    return (100 * Math.max(0, end - start)) / D.duration
  }
  const acceptedTimingCoveragePct = recordingCoveragePct(acceptedCycles)
  const anchorCycleMedian = median(acceptedCycles.map((c) => c.cycle))
  const anchorSysFracMedian = median(acceptedCycles.map((c) => c.sysFrac))
  const researchCandidates = (quality.usableCycles || []).filter((c) => {
    if (!anchorCycleMedian || !Number.isFinite(c.cycle) || !Number.isFinite(c.sysFrac)) return false
    const periodDelta = Math.abs(c.cycle - anchorCycleMedian) / anchorCycleMedian
    const phaseDelta = Math.abs(c.sysFrac - anchorSysFracMedian)
    return periodDelta <= 0.2 && phaseDelta <= 0.15
  })
  const candidateCoveragePct = recordingCoveragePct(researchCandidates)
  // Upstream v0.9 scores Energy over the accepted Timing cycles only (scope
  // policy `exact_evaluation_range_timing_cycles`): the research extension to
  // usable-but-unaccepted candidates is off. Upstream's extension test is kept
  // behind that switch so the candidate measurements read as before.
  const exactEvalRangeEnergy = true
  const useResearchExtension =
    !exactEvalRangeEnergy &&
    acceptedCycles.length >= 3 &&
    acceptedTimingCoveragePct < 60 &&
    researchCandidates.length > acceptedCycles.length &&
    candidateCoveragePct >= 60
  const cycles = useResearchExtension ? researchCandidates : acceptedCycles
  const configuredLow = Number.parseInt(String(bandKey).replace(/^s/, ""), 10)
  const configuredRange = Number.isFinite(configuredLow) ? [configuredLow, configuredLow + 200] : [100, 300]
  const band = ENERGY_BANDS[bandKey] || null
  // Build every selected window from the same locally smoothed frequency
  // representation. The previous three-band mean still allowed a 5 Hz
  // movement to add or remove a threshold-driving edge band. A fixed
  // triangular kernel makes adjacent 200 Hz windows differ gradually while
  // keeping the selected centre band most influential.
  const frequencyKernel = [
    { offset: -20, weight: 1 },
    { offset: -15, weight: 2 },
    { offset: -10, weight: 3 },
    { offset: -5, weight: 4 },
    { offset: 0, weight: 5 },
    { offset: 5, weight: 4 },
    { offset: 10, weight: 3 },
    { offset: 15, weight: 2 },
    { offset: 20, weight: 1 },
  ]
  const neighborBands = Number.isFinite(configuredLow)
    ? frequencyKernel
        .map((item) => {
          const requestedLow = configuredLow + item.offset
          // Do not clamp out-of-range neighbors onto the 35 or 250 Hz
          // boundary. That duplicated and overweighted edge bands.
          if (requestedLow < 35 || requestedLow > 250) return null
          const candidate = ENERGY_BANDS[energySweepKey(requestedLow)]
          return candidate &&
            candidate.trace &&
            candidate.trace.length === (band && band.trace ? band.trace.length : -1)
            ? { trace: candidate.trace, weight: item.weight }
            : null
        })
        .filter(Boolean)
    : []
  const kernelWeight = neighborBands.reduce((sum, item) => sum + item.weight, 0) || 1
  const trace =
    band && neighborBands.length
      ? Float32Array.from(
          band.trace,
          (_, i) => neighborBands.reduce((sum, item) => sum + item.weight * item.trace[i], 0) / kernelWeight
        )
      : band
      ? band.trace
      : new Float32Array(0)
  const emptySeries = new Float32Array(trace.length || 0)
  const base = {
    schema: "medaica.pcg.coherent_phase_energy.v1.8",
    status: "unavailable",
    researchOnly: true,
    bandKey,
    rangeHz: band ? band.rangeHz : configuredRange,
    backgroundSeries: trace,
    series: emptySeries,
    selectedTimingCycleIds: acceptedCycleIds,
    scoredTimingCycleIds: cycleAuditIds(cycles),
    timingBasis: useResearchExtension ? "research_extended_usable_timing_candidates" : "accepted_timing_cycles",
    energyScopePolicy: exactEvalRangeEnergy
      ? "exact_evaluation_range_timing_cycles"
      : "legacy_research_extension_allowed",
    acceptedTimingCoveragePct,
    researchTimingCoveragePct: recordingCoveragePct(cycles),
    scoredCycles: cycles.length,
    sysRecurrencePct: 0,
    diaRecurrencePct: 0,
    coherentPhaseCoveragePct: 0,
    proposedSys: "N/A",
    proposedDia: "N/A",
    reason: band
      ? "Needs at least 3 Timing cycles for the selected research basis"
      : "Research band unavailable for this browser-only source",
  }
  if (!trace.length || !D.duration || cycles.length < 3) {
    ctx.cache.coherentEnergyV11[bandKey] = base
    return base
  }
  const binsPerPhase = 24
  const exclusionPad =
    TURBULENT_RF_MODEL && typeof TURBULENT_RF_MODEL.s1s2_exclusion_pad_s === "number"
      ? TURBULENT_RF_MODEL.s1s2_exclusion_pad_s
      : 0.05
  const phaseRows = { sys: [], dia: [] }
  const texturePhaseRows = { sys: [], dia: [] }
  const textureTraces = Object.values(ENERGY_TEXTURE_BANDS)
    .map((item) => item.trace)
    .filter((item) => item && item.length === trace.length)
  const windows = []
  function binWindow(start, end, sourceTrace = trace) {
    const row = []
    for (let b = 0; b < binsPerPhase; b++) {
      const a = start + ((end - start) * b) / binsPerPhase
      const z = start + ((end - start) * (b + 1)) / binsPerPhase
      const vals = valuesForWindow(sourceTrace, a, z, D.duration)
      row.push(vals.length ? mean(vals) : 0)
    }
    return row
  }
  cycles.forEach((c) => {
    const sysStart = Math.min(c.s2.t, (c.s1.off ?? c.s1.t) + exclusionPad)
    const sysEnd = Math.max(sysStart, (c.s2.on ?? c.s2.t) - exclusionPad)
    const diaStart = Math.min(c.nextS1.t, (c.s2.off ?? c.s2.t) + exclusionPad)
    const diaEnd = Math.max(diaStart, (c.nextS1.on ?? c.nextS1.t) - exclusionPad)
    phaseRows.sys.push(binWindow(sysStart, sysEnd))
    phaseRows.dia.push(binWindow(diaStart, diaEnd))
    texturePhaseRows.sys.push(textureTraces.flatMap((textureTrace) => binWindow(sysStart, sysEnd, textureTrace)))
    texturePhaseRows.dia.push(textureTraces.flatMap((textureTrace) => binWindow(diaStart, diaEnd, textureTrace)))
    // Qualification deliberately uses the trimmed windows above so the
    // normal S1/S2 transients cannot create an Energy candidate. Keep the
    // anatomical phase boundaries as separate display metadata: once a
    // recurring region is established beside a boundary, the guide may
    // continue toward that boundary without changing qualification.
    windows.push({
      phase: "sys",
      start: sysStart,
      end: sysEnd,
      boundaryStart: c.s1.t,
      boundaryEnd: c.s2.t,
    })
    windows.push({
      phase: "dia",
      start: diaStart,
      end: diaEnd,
      boundaryStart: c.s2.t,
      boundaryEnd: c.nextS1.t,
    })
  })
  const all = phaseRows.sys.flat().concat(phaseRows.dia.flat())
  const globalThreshold = adaptiveEnergyThreshold(all)
  const quietReference = percentile(all, 0.2)
  const limitedRequiredHits =
    cycles.length <= 4 ? cycles.length : Math.max(3, Math.ceil(cycles.length * (cycles.length < 8 ? 0.6 : 0.5)))
  const strongRequiredHits =
    cycles.length <= 4 ? cycles.length : Math.max(limitedRequiredHits, Math.ceil(cycles.length * 0.7))
  const consensus = { sys: [], dia: [] }
  const recurrence = { sys: [], dia: [] }
  const persistence = { sys: [], dia: [] }
  ;["sys", "dia"].forEach((phase) => {
    for (let b = 0; b < binsPerPhase; b++) {
      let hits = 0
      const hitIndexes = []
      phaseRows[phase].forEach((row, cycleIndex) => {
        const local25 = percentile(row, 0.25)
        const local75 = percentile(row, 0.75)
        const localScale = Math.max(0.035, local75 - local25)
        let tolerantPeak = 0
        for (let k = Math.max(0, b - 2); k <= Math.min(binsPerPhase - 1, b + 2); k++)
          tolerantPeak = Math.max(tolerantPeak, row[k])
        const localSnr = (tolerantPeak - local25) / localScale
        if (localSnr >= 0.75 && tolerantPeak >= globalThreshold) {
          hits += 1
          hitIndexes.push(cycleIndex)
        }
      })
      const lastIndex = Math.max(0, cycles.length - 1)
      const firstThirdEnd = Math.max(0, Math.ceil(cycles.length / 3) - 1)
      const lastThirdStart = Math.max(0, Math.floor((2 * cycles.length) / 3))
      const reachesFirstThird = hitIndexes.some((i) => i <= firstThirdEnd)
      const reachesLastThird = hitIndexes.some((i) => i >= lastThirdStart)
      const temporalSpan =
        hitIndexes.length > 1 && lastIndex ? (hitIndexes[hitIndexes.length - 1] - hitIndexes[0]) / lastIndex : 0
      const distributed =
        cycles.length <= 4 ? hits === cycles.length : reachesFirstThird && reachesLastThird && temporalSpan >= 0.6
      const strong = hits >= strongRequiredHits && distributed
      const limited = hits >= limitedRequiredHits && distributed
      recurrence[phase].push(hits / cycles.length)
      persistence[phase].push({ hits, distributed, strong, limited, temporalSpan })
      consensus[phase].push(strong || limited)
    }
    // Bridge one-bin gaps and expand two neighboring bins so an audible
    // sustained whoosh is not reduced to only its highest-energy centre.
    // This widens an already-qualified phase region; it does not relax
    // the recurrence rule that qualifies the region in the first place.
    const raw = consensus[phase].slice()
    consensus[phase] = raw.map(
      (on, b, arr) =>
        on || (!!arr[b - 1] && !!arr[b + 1]) || !!arr[b - 1] || !!arr[b + 1] || !!arr[b - 2] || !!arr[b + 2]
    )
    consensus[phase] = consensus[phase].map((on, b, arr) => on && (!!arr[b - 1] || !!arr[b + 1]))
  })
  const morphologyMatches = { sys: null, dia: null }
  let morphologyRecovered = false
  let morphologyPhase = null
  let morphologyClusterCycles = 0
  function normalizedShape(row) {
    const lo = percentile(row, 0.25)
    const hi = percentile(row, 0.75)
    const scale = Math.max(0.035, hi - lo)
    return row.map((v) => Math.max(0, Math.min(6, (v - lo) / scale)))
  }
  function normalizedTextureShape(row) {
    if (!textureTraces.length) return normalizedShape(row)
    const shape = []
    for (let channel = 0; channel < textureTraces.length; channel++) {
      shape.push(...normalizedShape(row.slice(channel * binsPerPhase, (channel + 1) * binsPerPhase)))
    }
    return shape
  }
  function shapeCorrelation(a, b) {
    if (!a.length || a.length !== b.length) return 0
    const am = mean(a),
      bm = mean(b)
    let dot = 0,
      aa = 0,
      bb = 0
    for (let i = 0; i < a.length; i++) {
      const av = a[i] - am,
        bv = b[i] - bm
      dot += av * bv
      aa += av * av
      bb += bv * bv
    }
    return aa > 1e-9 && bb > 1e-9 ? dot / Math.sqrt(aa * bb) : 0
  }
  function tolerantShapeCorrelation(a, b) {
    let best = -1
    for (let shift = -2; shift <= 2; shift++) {
      const av = [],
        bv = []
      for (let i = 0; i < a.length; i++) {
        const j = i + shift
        if (j >= 0 && j < b.length) {
          av.push(a[i])
          bv.push(b[j])
        }
      }
      best = Math.max(best, shapeCorrelation(av, bv))
    }
    return best
  }
  function distributedIndexes(indexes) {
    if (cycles.length <= 4) return indexes.length === cycles.length
    if (indexes.length < limitedRequiredHits) return false
    const firstThirdEnd = Math.max(0, Math.ceil(cycles.length / 3) - 1)
    const lastThirdStart = Math.max(0, Math.floor((2 * cycles.length) / 3))
    const span = (indexes[indexes.length - 1] - indexes[0]) / Math.max(1, cycles.length - 1)
    return indexes.some((i) => i <= firstThirdEnd) && indexes.some((i) => i >= lastThirdStart) && span >= 0.6
  }
  let coherentBins = consensus.sys.filter(Boolean).length + consensus.dia.filter(Boolean).length
  let coveragePct = (100 * coherentBins) / (2 * binsPerPhase)
  let diffuse = coveragePct > 55
  if (textureTraces.length || diffuse || !coherentBins) {
    ;["sys", "dia"].forEach((phase) => {
      const shapeRows = textureTraces.length ? texturePhaseRows[phase] : phaseRows[phase]
      const shapes = shapeRows.map(textureTraces.length ? normalizedTextureShape : normalizedShape)
      let best = []
      for (let seed = 0; seed < shapes.length; seed++) {
        const cluster = []
        for (let i = 0; i < shapes.length; i++)
          if (tolerantShapeCorrelation(shapes[seed], shapes[i]) >= 0.58) cluster.push(i)
        if (cluster.length > best.length && distributedIndexes(cluster)) best = cluster
      }
      if (best.length < limitedRequiredHits) return
      const template = []
      for (let b = 0; b < ((shapes[0] && shapes[0].length) || binsPerPhase); b++)
        template.push(median(best.map((i) => shapes[i][b])))
      const timeTemplate = textureTraces.length
        ? Array.from({ length: binsPerPhase }, (_, b) =>
            Math.max(...textureTraces.map((_, channel) => template[channel * binsPerPhase + b]))
          )
        : template
      const templateCutoff = Math.max(0.8, percentile(timeTemplate, 0.68))
      const rawMask = timeTemplate.map((v) => v >= templateCutoff)
      const mask = rawMask.map((on, b, arr) => on || !!arr[b - 1] || !!arr[b + 1])
      if (mask.filter(Boolean).length < 2) return
      consensus[phase] = mask
      const matches = []
      for (let i = 0; i < shapes.length; i++) if (tolerantShapeCorrelation(template, shapes[i]) >= 0.45) matches.push(i)
      morphologyMatches[phase] = new Set(matches)
      morphologyRecovered = true
      if (best.length > morphologyClusterCycles) {
        morphologyClusterCycles = best.length
        morphologyPhase = phase
      }
    })
    const morphologyMaskCoverage =
      (100 * (consensus.sys.filter(Boolean).length + consensus.dia.filter(Boolean).length)) / (2 * binsPerPhase)
    if (morphologyRecovered && morphologyMaskCoverage > 55 && morphologyMatches.sys && morphologyMatches.dia) {
      const keepPhase = morphologyMatches.sys.size >= morphologyMatches.dia.size ? "sys" : "dia"
      const dropPhase = keepPhase === "sys" ? "dia" : "sys"
      consensus[dropPhase] = Array(binsPerPhase).fill(false)
      morphologyMatches[dropPhase] = null
      morphologyPhase = keepPhase
      morphologyClusterCycles = morphologyMatches[keepPhase].size
    }
    coherentBins = consensus.sys.filter(Boolean).length + consensus.dia.filter(Boolean).length
    coveragePct = (100 * coherentBins) / (2 * binsPerPhase)
    diffuse = coveragePct > 55
  }
  const series = new Float32Array(trace.length)
  let boundaryContinuationApplied = false
  let boundaryContinuationWindows = 0
  if (!diffuse && coherentBins) {
    windows.forEach((w, windowIndex) => {
      const mask = consensus[w.phase]
      const cycleIndex = Math.floor(windowIndex / 2)
      if (morphologyMatches[w.phase] && !morphologyMatches[w.phase].has(cycleIndex)) return
      const row = phaseRows[w.phase][cycleIndex] || []
      const local25 = percentile(row, 0.25)
      const local75 = percentile(row, 0.75)
      const localScale = Math.max(0.035, local75 - local25)
      for (let b = 0; b < binsPerPhase; b++) {
        if (!mask[b]) continue
        const start = w.start + ((w.end - w.start) * b) / binsPerPhase
        const end = w.start + ((w.end - w.start) * (b + 1)) / binsPerPhase
        const i0 = Math.max(0, Math.floor((start / D.duration) * trace.length))
        const i1 = Math.min(trace.length, Math.ceil((end / D.duration) * trace.length))
        // Once recurrence has established a coherent phase region,
        // retain quieter occurrences in individual cycles. The
        // global recurrence gate above still prevents random noise
        // from creating a candidate region.
        const displayFloor = Math.max(quietReference, local25 + 0.1 * localScale)
        for (let i = i0; i < i1; i++) if (trace[i] >= displayFloor) series[i] = trace[i]
      }
      // A holosystolic, early-diastolic, or continuous acoustic pattern
      // can meet S1/S2. Extend only from an already-qualified edge of
      // the phase mask. The excluded boundary region never participates
      // in recurrence, morphology, strength, or phase qualification.
      const edgeSpan = Math.max(1, Math.ceil(binsPerPhase * 0.12))
      const continuesAtStart = mask.slice(0, edgeSpan).filter(Boolean).length >= 2
      const continuesAtEnd = mask.slice(-edgeSpan).filter(Boolean).length >= 2
      const copyBoundaryTrace = (start, end, referenceBin) => {
        if (!(end > start)) return
        const i0 = Math.max(0, Math.floor((start / D.duration) * trace.length))
        const i1 = Math.min(trace.length, Math.ceil((end / D.duration) * trace.length))
        const edgeValue = Number.isFinite(row[referenceBin]) ? row[referenceBin] : local25
        // Continuation remains visually sensitive after recurrence is
        // established, but requires energy comparable with the
        // adjacent qualified bin rather than exposing all S1/S2 energy.
        const boundaryFloor = Math.max(quietReference, local25 + 0.1 * localScale, 0.55 * edgeValue)
        let copied = false
        for (let i = i0; i < i1; i++)
          if (trace[i] >= boundaryFloor) {
            series[i] = trace[i]
            copied = true
          }
        if (copied) {
          boundaryContinuationApplied = true
          boundaryContinuationWindows += 1
        }
      }
      if (continuesAtStart) copyBoundaryTrace(w.boundaryStart, w.start, 0)
      if (continuesAtEnd) copyBoundaryTrace(w.end, w.boundaryEnd, binsPerPhase - 1)
    })
  }
  function phaseSummary(phase) {
    const peakRecurrence = recurrence[phase].length ? Math.max(...recurrence[phase]) : 0
    const kept = []
    consensus[phase].forEach((on, b) => {
      if (on) phaseRows[phase].forEach((row) => kept.push(row[b]))
    })
    const tier = kept.length ? energyStrengthTier(percentile(kept, 0.75)).label : "N/A"
    return { peakRecurrencePct: 100 * peakRecurrence, tier }
  }
  const sys = phaseSummary("sys"),
    dia = phaseSummary("dia")
  const hasStrongPersistence = ["sys", "dia"].some((phase) => persistence[phase].some((item) => item.strong))
  const status = diffuse
    ? "noise_limited"
    : coherentBins
    ? hasStrongPersistence
      ? "phase_coherent"
      : "phase_coherent_limited"
    : "no_repeatable_phase_activity"
  ctx.cache.coherentEnergyV11[bandKey] = {
    ...base,
    status,
    series,
    boundaryContinuationApplied,
    boundaryContinuationWindows,
    boundaryContinuationPolicy:
      "display_only_after_adjacent_phase_recurrence; excluded S1/S2 regions cannot qualify Energy",
    threshold: globalThreshold,
    quietReference,
    requiredCycleHits: limitedRequiredHits,
    strongRequiredCycleHits: strongRequiredHits,
    morphologyRecovered,
    morphologyPhase,
    morphologyClusterCycles,
    morphologyFeatureBands: Object.values(ENERGY_TEXTURE_BANDS).map((item) => item.rangeHz),
    sysRecurrencePct: sys.peakRecurrencePct,
    diaRecurrencePct: dia.peakRecurrencePct,
    coherentPhaseCoveragePct: coveragePct,
    proposedSys: status === "phase_coherent" || status === "phase_coherent_limited" ? sys.tier : "N/A",
    proposedDia: status === "phase_coherent" || status === "phase_coherent_limited" ? dia.tier : "N/A",
    reason: diffuse
      ? "Diffuse activity spans too much of the normalized cardiac phase axis"
      : morphologyRecovered
      ? `A distributed ${
          morphologyPhase === "sys" ? "systolic" : "diastolic"
        } time-frequency envelope cluster matched ${morphologyClusterCycles}/${
          cycles.length
        } research-scored cycles; quieter correlated occurrences were retained`
      : coherentBins
      ? `${
          hasStrongPersistence ? "Strong" : "Limited"
        } recurring activity was distributed from the first through final third of the ${
          useResearchExtension ? "research Timing candidate" : "accepted Timing"
        } cycles`
      : `No phase position met both the ${limitedRequiredHits}/${cycles.length} recurrence floor and temporal-distribution rule`,
  }
  return ctx.cache.coherentEnergyV11[bandKey]
}

// The kernel-blended tail of the prototype's visibleEnergyGuide. The exported
// visibleEnergyGuide (./marker-overlap.js) adds the handoff v0.8.3 marker-overlap
// preview head in front of this and delegates here otherwise.
export function coherentEnergyGuide(ctx, bandKey = "s100", energyGuideMode = "coherent") {
  if (energyGuideMode !== "coherent") return ctx.murmurTrace
  const ENERGY_BANDS = energyBandMap(ctx)
  const configuredLow = Number.parseInt(String(bandKey || "s100").replace(/^s/, ""), 10)
  const center = coherentEnergyV1(ctx, bandKey).series
  if (!Number.isFinite(configuredLow) || !center || !center.length) return center
  // Interpolate qualified overlays instead of taking the maximum from a few
  // neighboring bands. This produces a continuous visual sweep: moving the
  // 200 Hz window by 5 Hz changes the contribution weights but cannot make
  // one neighboring result suddenly dominate the whole display. Audit and
  // provisional-result values remain those of the selected centre band.
  const neighbors = [
    { offset: -20, weight: 1 },
    { offset: -15, weight: 2 },
    { offset: -10, weight: 3 },
    { offset: -5, weight: 4 },
    { offset: 0, weight: 5 },
    { offset: 5, weight: 4 },
    { offset: 10, weight: 3 },
    { offset: 15, weight: 2 },
    { offset: 20, weight: 1 },
  ]
    .map((item) => {
      const requestedLow = configuredLow + item.offset
      if (requestedLow < 35 || requestedLow > 250) return null
      const key = energySweepKey(requestedLow)
      const result = ENERGY_BANDS[key] ? coherentEnergyV1(ctx, key) : null
      return result && result.series && result.series.length === center.length
        ? { series: result.series, weight: item.weight }
        : null
    })
    .filter(Boolean)
  if (neighbors.length <= 1) return center
  const totalWeight = neighbors.reduce((sum, item) => sum + item.weight, 0) || 1
  return Float32Array.from(
    center,
    (_, i) => neighbors.reduce((sum, item) => sum + item.weight * (item.series[i] || 0), 0) / totalWeight
  )
}

export function recommendedEnergyBand(ctx) {
  const ENERGY_BANDS = energyBandMap(ctx)
  const strengthRank = { High: 3, Moderate: 2, Low: 1, "N/A": 0 }
  const candidates = Object.keys(ENERGY_BANDS)
    .map((key) => {
      const result = coherentEnergyV1(ctx, key)
      if (result.status !== "phase_coherent" && result.status !== "phase_coherent_limited") return null
      const recurrence = Math.max(result.sysRecurrencePct || 0, result.diaRecurrencePct || 0)
      const strength = Math.max(strengthRank[result.proposedSys] || 0, strengthRank[result.proposedDia] || 0)
      let displaySamples = 0
      if (result.series) for (let i = 0; i < result.series.length; i++) if (result.series[i] > 0) displaySamples += 1
      const displayCoverage = result.series && result.series.length ? (100 * displaySamples) / result.series.length : 0
      if (!displaySamples) return null
      const statusWeight = result.status === "phase_coherent" ? 400 : 200
      return {
        key,
        result,
        displayCoverage,
        score: statusWeight + 50 * strength + recurrence + 3 * displayCoverage + (result.morphologyRecovered ? 15 : 0),
      }
    })
    .filter(Boolean)
  if (!candidates.length) return ENERGY_BANDS.s100 ? "s100" : Object.keys(ENERGY_BANDS)[0] || "s100"
  const bestScore = Math.max(...candidates.map((candidate) => candidate.score))
  // Treat very small score differences as equivalent and prefer the
  // established 100–300 Hz reference neighbourhood in that case.
  return candidates
    .filter((candidate) => candidate.score >= bestScore - 5)
    .sort(
      (a, b) =>
        Math.abs(((a.result.rangeHz && a.result.rangeHz[0]) || 100) - 100) -
        Math.abs(((b.result.rangeHz && b.result.rangeHz[0]) || 100) - 100)
    )[0].key
}
